import {cx, ClassNamesArg} from '@emotion/css';
import {StyleOverrides} from '@fo/shared-types';

type FunctionStyle = (...args: never[]) => ClassNamesArg;
type StyleClass = ClassNamesArg | FunctionStyle;
type StylesSet = {[key: string]: StyleClass};

const isFunctionStyle = (styleClass: StyleClass): styleClass is FunctionStyle => styleClass instanceof Function;

const isStylesSet = <TStyles>(styles: StyleOverrides<TStyles> | undefined): styles is StyleOverrides<TStyles> =>
  !!styles;

const getClassNamesArg = (style: StyleClass, ...args: never[]): ClassNamesArg =>
  isFunctionStyle(style) ? style(...args) : style;

const getCombinedClassName = (originalStyle: StyleClass, overridingStyle: StyleClass) => {
  const combinedClassNameFunction = (...args: never[]) =>
    cx(getClassNamesArg(originalStyle, ...args), getClassNamesArg(overridingStyle, ...args));

  return isFunctionStyle(originalStyle) ? combinedClassNameFunction : combinedClassNameFunction();
};

const getOverriddenStyleSet = <TStyles extends StylesSet>(styles: TStyles, styleOverride: StyleOverrides<TStyles>) => {
  const getCombinedClassNames = (
    updatedStyles: StyleOverrides<TStyles>,
    className: string
  ): StyleOverrides<TStyles> => ({
    ...updatedStyles,
    [className]: getCombinedClassName(updatedStyles[className], styleOverride[className]),
  });

  const classNames = Object.keys(styles);
  return classNames.reduce(getCombinedClassNames, styles);
};

const getCombinedStyles = <TStyles extends StylesSet>(
  originalStyles: TStyles,
  ...styleOverrides: (StyleOverrides<TStyles> | undefined)[]
): TStyles => {
  const definedStyleOverrides = styleOverrides.filter(isStylesSet);

  const overriddenStyles = definedStyleOverrides.reduce(getOverriddenStyleSet, originalStyles);

  return {
    ...originalStyles,
    ...overriddenStyles,
  };
};

export default getCombinedStyles;
